import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Login from './login/Login';
import NavBar from "./navbar/NavBar";
import Documents from "./documentation/Documents";
import UserProfile from "./user/UserProfile";
import Dashboard from "./Dashboard";
import Integrations from "./integrations/Integrations";
import WebhookDetail from "./integrations/WebhookDetail";
import Vector from "./admin/MilvusAdmin";
import { useUser } from './user/UserContext';
import PrivacyPolicy from './PrivacyPolicy';
import AgentsList from './agents/AgentsList';
import ConfigureAgent from "./agents/ConfigureAgent";
import { AgentForm } from "./agents/CreateAgent";
import LeadList from "./leads/LeadsList";
import Lead from "./leads/Lead";
import AccountsList from "./accounts/AccountsList";
import Account from "./accounts/Account";
import ServicesPage from "./services/ServicesPage";
import ServiceEditor from "./services/ServiceEditor";
import JobsPage from "./jobs/JobsPage";
import JobPage from "./jobs/JobPage";
import DeleteOrganization from "./user/DeleteOrganization";
import Sessions from "./sessions/SessionsList";
import Session from "./sessions/SessionAlt";
import Websites from "./websites/Websites";
import Website from "./websites/Website";
import Analytics from "./analytics/AgentAnalytics";
import WebsitePreview from "./agents/WebsitePreview";
import Questions from "./questions/Questions";
import QuestionView from "./questions/QuestionView";
import Admin from "./admin/Admin";
import ConversationView from "./admin/ConversationView";
import VisitorView from "./admin/VisitorView";
import LeadView from "./admin/LeadView";
import OrganizationDetailView from "./admin/OrganizationDetailView";
import { conLog } from './utils/conLog';
import { ToastContainer } from 'react-toastify';
import HotkeyNavigation from './HotkeyNavigation';
import Knowledge from './Knowledge';
import LiveView from './liveview/LiveView';
import AimdocLoader from './ui/AimdocLoader';
import { lazy, Suspense, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from './ThemeSwitcher';
import OAuthConsentScreen from './oauth/OAuthConsentScreen';
import OAuthCallback from './oauth/OAuthCallback';
import { Button } from "@/components/ui/button"
import Upgrade from './modals/Upgrade';
import OrganizationAccessDecision from './onboarding/OrganizationAccessDecision';
import AgentFromAI from './AgentFromAI';
import { isApplicationBootstrapPending } from '../lib/appBootstrap';

const TrialDogfood = import.meta.env.DEV
  ? lazy(() => import('./onboarding/TrialDogfood'))
  : null;

const LeadsRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/contacts/${id}`} replace />;
};

const Main = ({ onWidgetHiddenChange }: { onWidgetHiddenChange: (hidden: boolean) => void }) => {
  const location = useLocation();
  const {
    user,
    loading,
    organization,
    onboardingDecision,
    organizationOnboarding,
    bootstrapPhase,
    bootstrapError,
    checkUser,
    refreshUser,
    setOrganizationOnboarding,
  } = useUser();
  const { isDark } = useTheme();
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

  const search = new URLSearchParams(window.location.search);
  const sessionId = search.get('session_id');
  const upgradeSuccess = search.get('upgrade');
  // Stripe Checkout / in-place plan changes redirect to WEB_APP_URL?billing=…
  // (see api/routes/billing.py). Open the Upgrade modal so its success view
  // confirms the purchase wherever the customer lands.
  const billingStatus = search.get('billing');
  const billingSucceeded = billingStatus === 'success' || billingStatus === 'changed';
  const trialDogfoodAvailable = import.meta.env.DEV
    && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname.toLowerCase());
  const isTrialDogfoodRoute = trialDogfoodAvailable && location.pathname === '/trial-dogfood';
  const isAuthRoute =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/oauth-callback' ||
    (isTrialDogfoodRoute && trialDogfoodAvailable);
  const applicationBootstrapPending = isApplicationBootstrapPending({
    loading,
    hasOrganization: Boolean(organization),
    organizationOnboardingResolved: organizationOnboarding !== null,
  });

  useEffect(() => {
    if (sessionId || upgradeSuccess === 'success' || billingSucceeded) {
      setUpgradeModalVisible(true);
    }
  }, [sessionId, upgradeSuccess, billingSucceeded]);

  const isOrgDecisionRoute = location.pathname === '/onboarding/organization-choice';
  const requiresOnboardingDecision = Boolean(
    user &&
    !loading &&
    !organization &&
    onboardingDecision?.requires_decision
  );

  const shouldShowAgentOnboarding = Boolean(
    user &&
    organization &&
    !loading &&
    !onboardingDecision?.requires_decision &&
    (organizationOnboarding?.required || organizationOnboarding?.building) &&
    !isAuthRoute &&
    !location.pathname.includes('/agent-embed')
  );

  // The Aimdoc widget belongs to the app shell only. Onboarding stays
  // widget-free through the finished build screen too: the handoff
  // conversation is AI-initiated, so it opens on the dashboard via the
  // unseen-outbound reopen once the widget connects there.
  const showAimdocWidget = Boolean(
    user &&
    organization &&
    !applicationBootstrapPending &&
    bootstrapPhase !== 'error' &&
    !requiresOnboardingDecision &&
    !shouldShowAgentOnboarding &&
    !isAuthRoute &&
    !isOrgDecisionRoute &&
    !location.pathname.includes('/agent-embed') &&
    !location.pathname.includes('/oauth/consent')
  );
  useEffect(() => {
    onWidgetHiddenChange(!showAimdocWidget);
  }, [onWidgetHiddenChange, showAimdocWidget]);

  // Block app rendering until we resolve user/org onboarding flow to avoid route flashes.
  if (
    applicationBootstrapPending &&
    bootstrapPhase !== 'error' &&
    !isAuthRoute &&
    !location.pathname.includes('/agent-embed')
  ) {
    return <AimdocLoader label="Loading Aimdoc" />;
  }

  if (bootstrapPhase === 'error' && !isAuthRoute && !location.pathname.includes('/agent-embed')) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-md rounded-xl border bg-card p-7 text-center shadow-sm">
          <h1 className="text-lg font-semibold">We couldn’t load your workspace</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {bootstrapError || 'Check your connection and try again.'}
          </p>
          <Button className="mt-5" onClick={() => checkUser()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!user && !loading && !isAuthRoute && !location.pathname.includes('/agent/') && !location.pathname.includes('/agent-embed')) {
    conLog("User not logged in, redirecting to login page");
    return <Navigate to="/login" />;
  }

  if (requiresOnboardingDecision && !isOrgDecisionRoute) {
    return <Navigate to="/onboarding/organization-choice" replace />;
  }

  if (user && organization && !loading && !onboardingDecision?.requires_decision && isOrgDecisionRoute) {
    return <Navigate to="/" replace />;
  }

  if (shouldShowAgentOnboarding) {
    return (
      <>
        <ToastContainer />
        <AgentFromAI
          user={user}
          organization={organization}
          initialWebsiteUrl={organizationOnboarding?.default_website_url}
          state={organizationOnboarding}
          onStateChange={(next) => setOrganizationOnboarding(next)}
          onCompleted={(next) => {
            setOrganizationOnboarding({ ...next, required: false, building: false });
            refreshUser();
          }}
        />
      </>
    );
  }

  const selectedTab = localStorage.getItem('selectedTab');
  if (!selectedTab) {
    localStorage.setItem('selectedTab', 'sales');
  }


  return (
    <div>
      <HotkeyNavigation />
      <ToastContainer />
      <div className="h-full w-full flex overflow-hidden">
        {user && !isOrgDecisionRoute && !location.pathname.includes('/agent-embed') && !location.pathname.includes('/login') && !location.pathname.includes('/register') && !location.pathname.includes('/oauth/consent') && <NavBar selectedItem={location.pathname} onAddCredits={organization ? () => setUpgradeModalVisible(true) : undefined} />}
        {upgradeModalVisible && user && !isOrgDecisionRoute && !location.pathname.includes('/agent-embed') && !location.pathname.includes('/login') && !location.pathname.includes('/register') && !location.pathname.includes('/oauth/consent') && (
        <Upgrade 
          showModal={upgradeModalVisible} 
          setShowModal={setUpgradeModalVisible} 
          sessionId={sessionId} 
          upgradeSuccess={upgradeSuccess === 'success' || billingStatus === 'changed'}
        />
      )}

        <div className={`h-full w-full min-w-0 flex overflow-x-hidden main-content-area`}>
          <Routes>
            {user ? (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/liveview" element={<LiveView />} />
                <Route path="/knowledge" element={<Knowledge />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/user-settings" element={<UserProfile />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/integrations/webhooks/:id" element={<WebhookDetail />} />
                <Route path="/agents" element={<AgentsList />} />
                <Route path="/agents/:id" element={<ConfigureAgent />} />
                <Route path="/agents/new" element={<AgentForm setShowModal={() => {}} />} />
                <Route path="/websites" element={<Websites />} />
                <Route path="/websites/:id" element={<Website />} />
                <Route path="/contacts" element={<LeadList />} />
                <Route path="/contacts/:id" element={<Lead />} />
                <Route path="/accounts" element={<AccountsList />} />
                <Route path="/accounts/:id" element={<Account />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/new" element={<ServiceEditor />} />
                <Route path="/services/:id" element={<ServiceEditor />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/jobs/:id" element={<JobPage />} />
                {/* Events moved under Services (2026-08-23); keep old links working. */}
                <Route path="/events" element={<Navigate to="/services?tab=events" replace />} />
                <Route path="/leads" element={<Navigate to="/contacts" replace />} />
                <Route path="/leads/:id" element={<LeadsRedirect />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/sessions/:id" element={<Session />} />
                <Route path="/questions" element={<Questions />} />
                <Route path="/questions/:id" element={<QuestionView />} />
                {user?.is_admin && <Route path="/admin" element={<Admin />} />}
                {user?.is_admin && <Route path="/admin/organizations/:id" element={<OrganizationDetailView />} />}
                {user?.is_admin && <Route path="/admin/conversations/:id" element={<ConversationView />} />}
                {user?.is_admin && <Route path="/admin/visitors/:id" element={<VisitorView />} />}
                {user?.is_admin && <Route path="/admin/leads/:id" element={<LeadView />} />}
                {user?.is_admin && <Route path="/organizations/:id/delete" element={<DeleteOrganization />} />}
                <Route path="/oauth/consent" element={<OAuthConsentScreen />} />
                <Route path="/onboarding/organization-choice" element={<OrganizationAccessDecision />} />
              </>
            ) : null}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login />} />
            {trialDogfoodAvailable && TrialDogfood && (
              <Route
                path="/trial-dogfood"
                element={(
                  <Suspense fallback={<AimdocLoader label="Loading trial simulator" />}>
                    <TrialDogfood />
                  </Suspense>
                )}
              />
            )}
            <Route path="/oauth-callback" element={<OAuthCallback />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/agent-embed-preview/:agentId/:websiteUrl" element={<WebsitePreview />} />
            <Route path="/agent-embed-preview/:agentId" element={<WebsitePreview />} />
          </Routes>
        </div>
      </div>
    </div>
  )

};

export default Main;
